<template>
  <div
    class="flex justify-between items-center px-1 h-6.5 py-1"
    :class="[vertical ? 'w-1/2' : 'w-full']"
  >
    <NText weight="medium" variant="caption2">
      {{ t('option.contractSize') }}
    </NText>

    <NumberFormatter #="{ number }" :value="optionContract.contractSize">
      <NText weight="bold" variant="caption2">
        {{ number }}
      </NText>
    </NumberFormatter>
  </div>

  <div
    class="flex justify-between items-center px-1 h-6.5 py-1"
    :class="[vertical ? 'w-1/2' : 'w-full']"
  >
    <NText weight="medium" variant="caption2">
      {{ t('option.cashSettlementDueDate') }}
    </NText>

    <NText weight="bold" variant="caption2">
      {{ getFormattedDate(optionContract.cashSettlementDate as string) }}
    </NText>
  </div>

  <div
    class="flex justify-between items-center px-1 h-6.5 py-1"
    :class="[vertical ? 'w-1/2' : 'w-full']"
  >
    <NText weight="medium" variant="caption2">
      {{ t('option.physicalSettlementDueDate') }}
    </NText>

    <NText weight="bold" variant="caption2">
      {{ getFormattedDate(optionContract.physicalSettlementDate as string) }}
    </NText>
  </div>

  <div
    class="flex justify-between items-center px-1 h-6.5 py-1"
    :class="[vertical ? 'w-1/2' : 'w-full']"
  >
    <NText weight="medium" variant="caption2">
      {{ t('option.settlementDateDays') }}
    </NText>

    <NumberFormatter
      #="{ number }"
      :value="
        getTimeDifference({
        startTime: new Date(time).getTime(),
          endTime: new Date(
            optionContract.physicalSettlementDate as string
          ).getTime() + DAY_PER_MILLISECONDS,
        }).differenceInDays
      "
    >
      <NText weight="bold" variant="caption2">
        {{ number }}
      </NText>
    </NumberFormatter>
  </div>

  <div
    class="flex justify-between items-center px-1 h-6.5 py-1"
    :class="[vertical ? 'w-1/2' : 'w-full']"
  >
    <NText weight="medium" variant="caption2">
      {{ t('option.contractStatus') }}
    </NText>

    <NText
      weight="bold"
      variant="caption2"
      :class="{
        'text-neutral-secondary': contractReturnValue === 0,
        'text-critical-secondary': contractReturnValue < 0,
        'text-brand-primary': contractReturnValue > 0,
      }"
    >
      {{ useGetOptionContractStateLabel(contractReturnValue) }}
    </NText>
  </div>

  <div
    class="flex justify-between items-center px-1 h-6.5 py-1"
    :class="[vertical ? 'w-1/2' : 'w-full']"
  >
    <div class="inline-flex items-center">
      <NText weight="medium" variant="caption2">
        {{ t('option.strikePrice') }}
      </NText>
      <NText
        weight="medium"
        variant="caption2"
        class="text-neutral-secondary mr-1"
      >
        {{ t('misc.rial') }}
      </NText>
    </div>

    <NumberFormatter #="{ number }" :value="optionContract.strikePrice">
      <NText weight="bold" variant="caption2">
        {{ number }}
      </NText>
    </NumberFormatter>
  </div>

  <div
    class="flex justify-between items-center px-1 h-6.5 py-1"
    :class="[vertical ? 'w-1/2' : 'w-full']"
  >
    <div class="inline-flex items-center">
      <NText weight="medium" variant="caption2">
        {{ t('option.baseAssetPrice') }}
      </NText>
      <NText
        weight="medium"
        variant="caption2"
        class="text-neutral-secondary mr-1"
      >
        {{ t('misc.rial') }}
      </NText>
    </div>

    <div class="flex items-center">
      <NumberFormatter
        #="{ number }"
        :value="
          (optionContract.underlyingAssetReferencePriceVariationPercentage ??
            0) / 100
        "
        maximum-fraction-digits="2"
        sign-display="always"
        number-style="percent"
      >
        <NText
          weight="bold"
          variant="caption2"
          dir="ltr"
          class="ms-1"
          :class="{
            'text-brand-primary':
              (optionContract.underlyingAssetReferencePriceVariationPercentage ??
                0) /
                100 >
              0,
            'text-critical-primary':
              (optionContract.underlyingAssetReferencePriceVariationPercentage ??
                0) /
                100 <
              0,
          }"
        >
          ({{ number }})
        </NText>
      </NumberFormatter>

      <NumberFormatter
        #="{ number }"
        :value="optionContract.underlyingAssetLastTradePrice"
      >
        <NText weight="bold" variant="caption2">
          {{ number }}
        </NText>
      </NumberFormatter>
    </div>
  </div>

  <div
    class="flex justify-between items-center px-1 h-6.5 py-1"
    :class="[vertical ? 'w-1/2' : 'w-full']"
  >
    <NText weight="medium" variant="caption2">
      {{ t('option.marketOpenPositions') }}
    </NText>

    <NumberFormatter #="{ number }" :value="optionContract.marketOpenPositions">
      <NText weight="bold" variant="caption2">
        {{ number }}
      </NText>
    </NumberFormatter>
  </div>

  <div
    class="flex justify-between items-center px-1 h-6.5 py-1"
    :class="[vertical ? 'w-1/2' : 'w-full']"
  >
    <div class="inline-flex items-center">
      <NText weight="medium" variant="caption2">
        {{ t('option.initialMargin') }}
      </NText>

      <NText
        weight="medium"
        variant="caption2"
        class="text-neutral-secondary mr-1"
      >
        {{ t('misc.rial') }}
      </NText>
    </div>

    <NumberFormatter #="{ number }" :value="optionContract.initialMargin">
      <NText weight="bold" variant="caption2">
        {{ number }}
      </NText>
    </NumberFormatter>
  </div>

  <div
    class="flex justify-between items-center px-1 h-6.5 py-1"
    :class="[vertical ? 'w-1/2' : 'w-full']"
  >
    <div class="inline-flex items-center">
      <NText weight="medium" variant="caption2">
        {{ t('option.requiredMargin') }}
      </NText>

      <NText
        weight="medium"
        variant="caption2"
        class="text-neutral-secondary mr-1"
      >
        {{ t('misc.rial') }}
      </NText>
    </div>

    <NumberFormatter #="{ number }" :value="optionContract.requiredMargin">
      <NText weight="bold" variant="caption2">
        {{ number }}
      </NText>
    </NumberFormatter>
  </div>

  <div
    v-if="optionContract?.marketOpenPositionUpperLimit"
    class="flex justify-between items-center px-1 h-6.5 py-1"
  >
    <NText
      weight="medium"
      variant="caption2"
      :class="{
        'ml-1': vertical,
      }"
    >
      {{ t('modules.portfolio.marketOpenPositionUpperLimit') }}
    </NText>

    <NumberFormatter
      #="{ number }"
      :value="optionContract.marketOpenPositionUpperLimit"
      round
    >
      <NText weight="bold" variant="caption2">
        {{ number }}
      </NText>
    </NumberFormatter>
  </div>

  <div
    v-if="optionContract?.firmTraderOpenPositionUpperLimit"
    class="flex justify-between items-center px-1 h-6.5 py-1"
  >
    <NText
      weight="medium"
      variant="caption2"
      :class="{
        'ml-1': vertical,
      }"
    >
      {{ t('modules.portfolio.firmTraderOpenPositionUpperLimit') }}
    </NText>

    <NumberFormatter
      #="{ number }"
      :value="optionContract.firmTraderOpenPositionUpperLimit"
      round
    >
      <NText weight="bold" variant="caption2">
        {{ number }}
      </NText>
    </NumberFormatter>
  </div>

  <div
    v-if="optionContract?.individualTraderOpenPositionUpperLimit"
    class="flex justify-between items-center px-1 h-6.5 py-1"
  >
    <NText
      weight="medium"
      variant="caption2"
      :class="{
        'ml-1': vertical,
      }"
    >
      {{ t('modules.portfolio.individualTraderOpenPositionUpperLimit') }}
    </NText>

    <NumberFormatter
      #="{ number }"
      :value="optionContract.individualTraderOpenPositionUpperLimit"
      round
    >
      <NText weight="bold" variant="caption2">
        {{ number }}
      </NText>
    </NumberFormatter>
  </div>

  <div
    v-if="optionContract?.sameRiskPositions"
    class="flex justify-between items-center px-1 h-6.5 py-1"
  >
    <NText
      weight="medium"
      variant="caption2"
      :class="{
        'ml-1': vertical,
      }"
    >
      {{ t('option.sameRiskPositions') }}
    </NText>

    <NumberFormatter
      #="{ number }"
      :value="optionContract.sameRiskPositions"
      round
    >
      <NText weight="bold" variant="caption2">
        {{ number }}
      </NText>
    </NumberFormatter>
  </div>

  <div
    v-if="optionContract?.delta"
    class="flex justify-between items-center px-1 h-6.5 py-1"
  >
    <NText
      weight="medium"
      variant="caption2"
      :class="{
        'ml-1': vertical,
      }"
    >
      {{ t('option.delta') }}
    </NText>

    <NumberFormatter #="{ number }" :value="optionContract.delta" round>
      <NText weight="bold" variant="caption2">
        {{ number }}
      </NText>
    </NumberFormatter>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { OptionContract } from '@libs/api-reference';
import { convertFaDigitsToEn, getTimeDifference } from '@libs/utils';
import { useGetServerClock, ORDER_SIDES } from '@core/order';
import { DAY_PER_MILLISECONDS } from '@core/option';
import { useGetOptionContractStateLabel } from '@/modules/option';

import NumberFormatter from '@/components/Number.vue';

type SymbolOptionDetailPropsType = {
  optionContract: OptionContract;
  vertical: boolean;
};

const props = defineProps<SymbolOptionDetailPropsType>();

const { t, d } = useI18n();

const { time } = useGetServerClock();

const getFormattedDate = (date: string) => convertFaDigitsToEn(d(date));

const isBuyContract = computed(
  () => props.optionContract?.contractType === ORDER_SIDES.buy
);

const contractReturnValue = computed(() => {
  if (isBuyContract.value) {
    return (
      props.optionContract?.underlyingAssetLastTradePrice -
      (1 - props.optionContract?.cashSettlementFee) *
        props.optionContract?.strikePrice
    );
  } else {
    return (
      (1 - props.optionContract?.cashSettlementFee) *
        props.optionContract?.strikePrice -
      props.optionContract?.underlyingAssetLastTradePrice
    );
  }
});
</script>
